import React, { useState } from 'react';
import PageTemplate from '../components/PageTemplate';

const FAQPage = () => {
  // State to track which FAQ items are expanded
  const [expandedItems, setExpandedItems] = useState({});

  // Toggle the expanded state of an FAQ item
  const toggleItem = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const faqCategories = [
    {
      id: 'general',
      title: 'General Questions',
      faqs: [
        {
          id: 'what-is',
          question: 'What is Patients Empowered?',
          answer: 'Patients Empowered is a breakthrough AI platform that transforms healthcare delivery by simultaneously empowering patients and supporting providers through integrated, intelligent solutions. Our platform addresses healthcare\'s three most critical challenges: provider burnout, information asymmetry, and medical errors.'
        },
        {
          id: 'how-works',
          question: 'What products does Patients Empowered offer?',
          answer: 'Patients Empowered offers two main products: 1) Patients Empowered Coach - an AI health companion for patients that prepares them for appointments through personalized symptom analysis, suggests critical questions, explains medical conditions, and provides continuous guidance; and 2) Patients Empowered Practice - AI-powered support for healthcare providers with clinical documentation, decision support, and patient insights.'
        },
        {
          id: 'cost',
          question: 'How much does Patients Empowered cost?',
          answer: 'Patients Empowered currently offers a free basic version of Patients Empowered Coach that includes the health form, AI chat, and PDF report generation. We offer premium features for both Patients Empowered Coach and Patients Empowered Practice with subscription plans tailored to individual patients and healthcare providers.'
        },
        {
          id: 'medical-advice',
          question: 'Does Patients Empowered provide medical advice?',
          answer: 'No, Patients Empowered does not provide medical advice, diagnosis, or treatment recommendations. Our platform is designed to help patients organize and communicate their health information more effectively with healthcare providers, and to support providers with clinical documentation and decision support. Always consult with a qualified healthcare professional for medical advice.'
        }
      ]
    },
    {
      id: 'using',
      title: 'Using the Platform',
      faqs: [
        {
          id: 'create-account',
          question: 'Do I need to create an account to use Patients Empowered?',
          answer: 'Currently, you can use the basic features of Patients Empowered Coach without creating an account. However, creating an account allows you to save your information for future visits and access your reports at any time. For healthcare providers, an account is required to access Patients Empowered Practice features.'
        },
        {
          id: 'save-info',
          question: 'Is my information saved between sessions?',
          answer: 'If you create an account, your information is securely saved and can be accessed for future visits. If you use Patients Empowered Coach without an account, your information is only stored temporarily during your session and is not accessible after you close your browser.'
        },
        {
          id: 'edit-report',
          question: 'Can I edit my report after it\'s generated?',
          answer: 'Currently, reports cannot be edited after generation. If you need to make changes, you would need to go through the process again with the updated information. We are working on adding editing capabilities in a future update for both Patients Empowered Coach and Patients Empowered Practice.'
        },
        {
          id: 'multiple-conditions',
          question: 'Can I use Patients Empowered Coach for multiple health conditions?',
          answer: 'Yes, you can use Patients Empowered Coach to prepare for appointments related to multiple health conditions. Our form and AI chat are designed to capture comprehensive information about all your health concerns and provide personalized guidance for each condition.'
        }
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy & Security',
      faqs: [
        {
          id: 'data-privacy',
          question: 'How is my health information protected?',
          answer: 'We take your privacy very seriously. All data is encrypted both in transit and at rest. We do not sell or share your personal health information with third parties. Our platform is designed with privacy as a core principle, and we implement industry-standard security measures to protect your data for both patients using Patients Empowered Coach and providers using Patients Empowered Practice.'
        },
        {
          id: 'hipaa',
          question: 'Is Patients Empowered HIPAA compliant?',
          answer: 'While Patients Empowered implements security measures, we are not currently a HIPAA-covered entity as we do not provide healthcare services or bill insurance. However, we follow many HIPAA-inspired best practices for data security and privacy.'
        },
        {
          id: 'data-usage',
          question: 'How is my data used?',
          answer: 'Your data is used solely to provide you with the Patients Empowered services. We may use anonymized, aggregated data to improve our AI models and services, but this never includes personally identifiable information. Our multi-model AI architecture processes your data securely to provide personalized insights and recommendations.'
        },
        {
          id: 'delete-data',
          question: 'Can I delete my data from your system?',
          answer: 'Yes, you can request deletion of your data at any time through your account settings or by contacting our support team. Upon request, we will delete all your personal information from our systems in accordance with our privacy policy and applicable regulations.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Support',
      faqs: [
        {
          id: 'browser-compatibility',
          question: 'Which browsers are supported?',
          answer: 'Patients Empowered works best on modern browsers including Chrome, Firefox, Safari, and Edge. We recommend keeping your browser updated to the latest version for the best experience with both Patients Empowered Coach and Patients Empowered Practice.'
        },
        {
          id: 'mobile-support',
          question: 'Can I use Patients Empowered on my mobile device?',
          answer: 'Yes, Patients Empowered Coach is fully responsive and works on smartphones and tablets. You can access all patient features through your mobile browser without needing to download an app. Patients Empowered Practice is optimized for desktop use in clinical settings but has mobile-responsive capabilities for on-the-go access.'
        },
        {
          id: 'pdf-issues',
          question: 'I\'m having trouble downloading my PDF report. What should I do?',
          answer: 'If you\'re experiencing issues downloading your PDF from Patients Empowered Coach, try the following: 1) Make sure your browser allows pop-ups from our site, 2) Try using a different browser, 3) Check your internet connection, or 4) Contact our support team for assistance. For Patients Empowered Practice users, please ensure you have the necessary permissions to download clinical documentation.'
        },
        {
          id: 'contact-support',
          question: 'How do I contact technical support?',
          answer: 'You can reach our support team by emailing support@patients-empowered.com or by using the contact form on our Support page. Our team typically responds within 24 hours on business days for both Patients Empowered Coach and Patients Empowered Practice users.'
        }
      ]
    }
  ];

  return (
    <PageTemplate title="Frequently Asked Questions">
      <div className="mb-5">
        <div className="card">
          <h2 className="mb-3">How can we help you?</h2>
          <input
            type="text"
            placeholder="Search for answers..."
            className="form-control"
          />
        </div>
      </div>

      <div className="mb-4">
        <div className="flex flex-wrap gap-md justify-center">
          {faqCategories.map(category => (
            <a
              key={category.id}
              href={`#${category.id}`}
              className="btn btn-secondary"
            >
              {category.title}
            </a>
          ))}
        </div>
      </div>

      {faqCategories.map(category => (
        <section key={category.id} id={category.id} className="mb-5">
          <h2 className="mb-4">{category.title}</h2>

          <div className="grid grid-cols-1 gap-md">
            {category.faqs.map(faq => (
              <div key={faq.id} className="card">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => toggleItem(faq.id)}
                >
                  <h3 className="mb-0">{faq.question}</h3>
                  <span className="text-xl">{expandedItems[faq.id] ? '−' : '+'}</span>
                </div>

                {expandedItems[faq.id] && (
                  <div className="mt-3 pt-3 border-top">
                    <p className="mb-0">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}

      <section className="text-center p-5 card">
        <h2 className="mb-3">Still have questions?</h2>
        <p className="mb-4">Our support team is here to help you with any questions or issues you may have.</p>
        <a href="/contact" className="btn btn-primary">Contact Us</a>
      </section>
    </PageTemplate>
  );
};

export default FAQPage;
